// A control that is working: aria-busy on the control, its label kept, and activation ignored until the work settles
// (DS #13 L). `busy` may come from the caller, or from run(work), which holds it while a promise is pending.

import { useCallback, useState } from "react";

export interface Busy {
  busy: boolean;
  /** Spread on the control. */
  props: { "aria-busy"?: true };
  /** Wraps a handler so it does nothing while busy. */
  guard: <A extends unknown[]>(handler?: ((...args: A) => void) | undefined) => (...args: A) => void;
  /** Holds busy while the work is pending. */
  run: <T>(work: PromiseLike<T>) => Promise<T>;
}

/**
 * Counts one more piece of work in flight until `work` settles, however it ends. The release is never guarded by a
 * mounted flag: React 18 ignores a state update after unmount, and such a guard could miss the release when
 * StrictMode's remount or a slow settle raced it. Exported for the unit test.
 */
export async function holdWhile<T>(set: (update: (n: number) => number) => void, work: PromiseLike<T>): Promise<T> {
  set((n) => n + 1);
  try {
    return await work;
  } finally {
    set((n) => n - 1);
  }
}

export function useBusy(external?: boolean | undefined): Busy {
  const [running, setRunning] = useState(0);
  const busy = Boolean(external) || running > 0;
  const guard = useCallback(<A extends unknown[]>(handler?: ((...args: A) => void) | undefined) => (...args: A) => {
    if (!busy) handler?.(...args);
  }, [busy]);
  const run = useCallback(<T,>(work: PromiseLike<T>): Promise<T> => holdWhile(setRunning, work), []);
  return { busy, props: busy ? { "aria-busy": true } : {}, guard, run };
}
